import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { motion, AnimatePresence, type HTMLMotionProps } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { createPortal } from 'react-dom';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Modal sizes
 */
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Props for the Modal component
 */
export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal content */
  children: ReactNode;
  /** Size of the modal */
  size?: ModalSize;
  /** Whether clicking the backdrop closes the modal */
  closeOnBackdropClick?: boolean;
  /** Whether pressing Escape closes the modal */
  closeOnEscape?: boolean;
  /** Show close button */
  showCloseButton?: boolean;
  /** Center the modal vertically */
  centered?: boolean;
}

/**
 * Props for ModalHeader component
 */
export interface ModalHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Props for ModalBody component
 */
export interface ModalBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Props for ModalFooter component
 */
export interface ModalFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Size styles for the modal
 */
const sizeStyles: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-[90vw] max-h-[90vh]',
};

/**
 * Animation variants for backdrop
 */
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

/**
 * Animation variants for modal
 */
const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    transition: {
      duration: 0.15,
    },
  },
};

/**
 * ModalHeader component
 */
export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-6 py-4',
        'border-b border-white/[0.08]',
        'text-lg font-semibold text-surface-900',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

ModalHeader.displayName = 'ModalHeader';

/**
 * ModalBody component
 */
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-6 py-4', 'text-surface-800 overflow-y-auto', className)}
      {...props}
    >
      {children}
    </div>
  )
);

ModalBody.displayName = 'ModalBody';

/**
 * ModalFooter component
 */
export const ModalFooter = forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-6 py-4',
        'border-t border-white/[0.08]',
        'flex items-center justify-end gap-3',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

ModalFooter.displayName = 'ModalFooter';

/**
 * Close button component
 */
const CloseButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  return (
  <motion.button
    type="button"
    onClick={onClick}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    className={cn(
      'absolute top-4 right-4',
      'p-2 rounded-lg',
      'bg-white/[0.06] hover:bg-white/[0.12]',
      'border border-white/[0.08] hover:border-white/[0.15]',
      'text-surface-500 hover:text-surface-800',
      'transition-colors duration-150',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
    )}
    aria-label={t('a11y.closeModal')}
  >
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  </motion.button>
  );
};

/**
 * Modal component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Modal open={isOpen} onClose={() => setIsOpen(false)} size="md">
 *   <ModalHeader>Confirm Action</ModalHeader>
 *   <ModalBody>
 *     Are you sure you want to proceed?
 *   </ModalBody>
 *   <ModalFooter>
 *     <Button variant="ghost" onClick={() => setIsOpen(false)}>Cancel</Button>
 *     <Button variant="primary">Confirm</Button>
 *   </ModalFooter>
 * </Modal>
 * ```
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open,
      onClose,
      children,
      size = 'md',
      closeOnBackdropClick = true,
      closeOnEscape = true,
      showCloseButton = true,
      centered = true,
      className,
      ...props
    },
    _ref
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Handle escape key
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === 'Escape' && closeOnEscape) {
          onClose();
        }
      },
      [closeOnEscape, onClose]
    );

    // Lock body scroll and manage focus — only auto-focus on initial open
    const wasOpen = useRef(false);
    useEffect(() => {
      if (open) {
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        // Only focus modal when it first opens, not on every re-render
        if (!wasOpen.current) {
          previousActiveElement.current = document.activeElement as HTMLElement;
          setTimeout(() => {
            modalRef.current?.focus();
          }, 0);
        }
        wasOpen.current = true;

        return () => {
          document.body.style.overflow = '';
          document.removeEventListener('keydown', handleKeyDown);
        };
      } else {
        if (wasOpen.current) {
          previousActiveElement.current?.focus();
        }
        wasOpen.current = false;
      }
    }, [open, handleKeyDown]);

    // Handle backdrop click
    const handleBackdropClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && closeOnBackdropClick) {
        onClose();
      }
    };

    // Portal content
    const modalContent = (
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-50">
            {/* Backdrop */}
            <motion.div
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2 }}
              className={cn(
                'fixed inset-0',
                'bg-surface-100/70 backdrop-blur-sm'
              )}
              onClick={handleBackdropClick}
              aria-hidden="true"
            />

            {/* Modal Container */}
            <div
              className={cn(
                'fixed inset-0 overflow-y-auto',
                'flex min-h-full p-4',
                centered ? 'items-center justify-center' : 'items-start justify-center pt-16'
              )}
              onClick={handleBackdropClick}
            >
              {/* Modal Content */}
              <motion.div
                ref={modalRef}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                role="dialog"
                aria-modal="true"
                tabIndex={-1}
                className={cn(
                  'relative w-full',
                  sizeStyles[size],
                  // Glass effect
                  'backdrop-blur-xl',
                  'bg-surface-100/90',
                  'border border-white/[0.12]',
                  'rounded-2xl',
                  'shadow-2xl shadow-black/50',
                  // Ring
                  'ring-1 ring-white/[0.05]',
                  className
                )}
                onClick={(e) => e.stopPropagation()}
                {...(props as Omit<HTMLMotionProps<'div'>, 'ref'>)}
              >
                {/* Gradient highlight */}
                <div
                  className="absolute inset-0 rounded-2xl opacity-50 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 50%)',
                  }}
                />

                {showCloseButton && <CloseButton onClick={onClose} />}
                {children}
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    );

    // Render via portal
    if (typeof document !== 'undefined') {
      return createPortal(modalContent, document.body);
    }

    return null;
  }
);

Modal.displayName = 'Modal';

export default Modal;
